import React, { useCallback, useEffect, useState } from 'react';
import { adminGetCacheStats, adminClearCache } from '../../api/admin';
import type { CacheStats } from '../../api/admin';
import { useToastStore } from '../../store/useToastStore';
import { TH, PERIOD_LABELS, fmtDate, fmtDuration, ConfirmDialog } from './shared';

export default function CacheTab() {
  const addToast = useToastStore((s) => s.addToast);
  const [stats, setStats] = useState<CacheStats | null>(null);
  const [confirmClear, setConfirmClear] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      setStats(await adminGetCacheStats());
    } catch {
      addToast('Fehler beim Laden der Cache-Übersicht.', 'danger');
    }
  }, [addToast]);

  // Laufende Fülläufe live verfolgen
  useEffect(() => {
    load();
    const id = setInterval(load, 5_000);
    return () => clearInterval(id);
  }, [load]);

  async function doClear() {
    setBusy(true);
    try {
      await adminClearCache();
      addToast('Cache geleert — Fülllauf startet automatisch neu.');
      setConfirmClear(false);
      await load();
    } catch {
      addToast('Fehler beim Leeren des Caches.', 'danger');
    } finally { setBusy(false); }
  }

  if (!stats) return <div className="text-center py-5"><span className="spinner-border" /></div>;

  const filling = stats.periods.filter((p) => p.warming).length;
  const ticker = stats.ticker;
  const now = Date.now();
  const fmtTime = (ms: number) => new Date(ms).toLocaleTimeString('de-DE');
  const inSecs = (ms: number) => Math.max(0, Math.round((ms - now) / 1000));
  const lastRuns = ticker?.history.slice(0, 12) ?? [];

  return (
    <>
      {/* ── Auto-Aktualisierung: Kompakt-Status (Details im Menüpunkt „Monitoring") ── */}
      {ticker && (
        <div className="border rounded-3 px-3 py-2 mb-4 d-flex align-items-center gap-3 flex-wrap" style={{ background: 'var(--bs-tertiary-bg)', fontSize: '0.82rem' }}>
          <span className="fw-semibold small">Auto-Aktualisierung</span>
          <span>⚡ nächster Schnell-Lauf in <strong>{inSecs(ticker.nextTickAt)}s</strong></span>
          <span>+{ticker.stats.totalNewRows.toLocaleString('de-DE')} neue Anrufe seit Start ({ticker.stats.totalRuns.toLocaleString('de-DE')} Läufe)</span>
          {lastRuns[0] && <span className="text-muted">letzter Lauf {fmtTime(lastRuns[0].at)}</span>}
          <span className="text-muted ms-auto">Lauf-Historie &amp; Filter → Menüpunkt „Monitoring"</span>
        </div>
      )}

      <div className="d-flex align-items-center gap-2 mb-3 flex-wrap">
        <span className="badge bg-primary-subtle text-primary-emphasis" style={{ fontSize: '0.8rem' }}>
          {stats.periods.length} Zeitraum-Cache{stats.periods.length !== 1 ? 's' : ''}
        </span>
        {filling > 0 && (
          <span className="badge bg-info-subtle text-info-emphasis" style={{ fontSize: '0.8rem' }}>
            {filling} Fülllauf{filling !== 1 ? 'e' : ''} aktiv
          </span>
        )}
        <span className="text-muted small">Aktualisiert alle 5 Sekunden · Fülllauf startet automatisch beim Start, alle 15 Min. und nach Konfig-Änderungen.</span>
        <button
          className="btn btn-outline-danger btn-sm ms-auto"
          disabled={busy || stats.periods.length === 0}
          onClick={() => setConfirmClear(true)}
        >
          Cache leeren
        </button>
      </div>

      <div className="table-responsive">
        <table className="table table-hover align-middle mb-0" style={{ fontSize: '0.83rem' }}>
          <thead style={TH}>
            <tr>
              <th className="ps-3">Zeitraum</th>
              <th>Quelle</th>
              <th className="text-end">Zeilen</th>
              <th>Stand bis</th>
              <th>Aktualisiert</th>
              <th className="pe-3" style={{ minWidth: 220 }}>Status</th>
            </tr>
          </thead>
          <tbody>
            {stats.periods.map((p, i) => (
              <tr key={i}>
                <td className="ps-3 fw-semibold">{PERIOD_LABELS[p.period] ?? p.period}</td>
                <td>
                  {p.node ?? <span className="text-muted">Global (Nummern-Filter)</span>}
                  <span className="text-muted ms-2" style={{ fontSize: '0.7rem' }}>{p.fieldsCount} Felder</span>
                </td>
                <td className="text-end">{(p.warming && p.rows === 0 ? p.warming.fetchedRows : p.rows).toLocaleString('de-DE')}</td>
                <td className="text-nowrap">{p.newest ? p.newest.slice(0, 16) : <span className="text-muted">—</span>}</td>
                <td className="text-nowrap">{fmtDate(new Date(p.updatedAt).toISOString())}</td>
                <td className="pe-3">
                  {p.warming ? (
                    <div style={{ minWidth: 200 }}>
                      <div className="d-flex align-items-center gap-2" style={{ fontSize: '0.78rem' }}>
                        <span className="spinner-border spinner-border-sm text-info" style={{ width: 12, height: 12 }} />
                        <span>
                          Füllt … {p.warming.fetchedRows.toLocaleString('de-DE')} Zeilen
                          {p.warming.percent !== null && <> · ~{Math.round(p.warming.percent * 100)} %</>}
                          {p.warming.etaSeconds !== null && <> · noch ~{fmtDuration(p.warming.etaSeconds * 1000)}</>}
                          <span className="text-muted"> · läuft {fmtDuration(p.warming.elapsedMs)}</span>
                        </span>
                      </div>
                      {p.warming.percent !== null && (
                        <div className="progress mt-1" style={{ height: 5 }}>
                          <div className="progress-bar progress-bar-striped progress-bar-animated bg-info" style={{ width: `${Math.max(3, Math.round(p.warming.percent * 100))}%` }} />
                        </div>
                      )}
                    </div>
                  ) : p.freshForMs > 0 ? (
                    <span className="badge bg-success-subtle text-success-emphasis">Frisch · noch {fmtDuration(p.freshForMs)}</span>
                  ) : (
                    <span className="badge bg-secondary-subtle text-secondary-emphasis" title="Beim nächsten Abruf wird nur das Delta seit dem letzten Stand nachgeladen">
                      Abgelaufen · Delta beim nächsten Abruf
                    </span>
                  )}
                </td>
              </tr>
            ))}
            {stats.periods.length === 0 && (
              <tr><td colSpan={6} className="text-center text-muted py-4">Noch keine Zeitraum-Caches — der Fülllauf startet kurz nach dem Backend-Start automatisch.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {confirmClear && (
        <ConfirmDialog
          title="Cache leeren?"
          text="Alle zwischengespeicherten Anrufdaten werden verworfen und automatisch neu geladen. Benutzer sehen währenddessen den Lade-Hinweis."
          confirmLabel="Leeren"
          busy={busy}
          onConfirm={doClear}
          onCancel={() => setConfirmClear(false)}
        />
      )}
    </>
  );
}
